import { useEffect, useRef, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Upload, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { uploadImages } from "../../api/upload.api.js";
import { assetUrl } from "../../api/client.js";
import { cn } from "../../lib/cn.js";

// Global constant for standard gallery categories
export const CATEGORY_OPTIONS = [
  { value: "general", label: "General" },
  { value: "kitchen", label: "Kitchen" },
  { value: "bathroom", label: "Bathroom" },
  { value: "bedroom", label: "Bedroom" },
  { value: "pool", label: "Pool & Outdoor" },
  { value: "living", label: "Living Areas" },
];

/**
 * Multi-image picker with drag-reorder. Stores an array of `url` paths and
 * supports image categorization with a batch category upload option.
 *
 *   <GalleryUpload
 *     value={form.images}
 *     onChange={(urls) => form.setValue("images", urls)}
 *     categories={form.imageCategories}
 *     onCategoriesChange={(cats) => form.setValue("imageCategories", cats)}
 *     subdir="designs"
 *     label="Gallery"
 *     max={20}
 *   />
 */
export function GalleryUpload({
  value = [],
  onChange,
  categories = {},
  onCategoriesChange = () => {},
  subdir,
  label,
  hint,
  max = 30,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("general");
  
  // Map of server URL → blob URL for in-session previews of files just
  // uploaded. Server URLs from a prior session don't appear here and fall
  // through to assetUrl().
  const [blobMap, setBlobMap] = useState({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    return () => {
      for (const blob of Object.values(blobMap)) {
        if (blob?.startsWith("blob:")) URL.revokeObjectURL(blob);
      }
    };
    // Cleanup on unmount only — we manage per-entry revocation in handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiles = async (filesList) => {
    const files = Array.from(filesList ?? []);
    if (files.length === 0) return;
    if (value.length + files.length > max) {
      toast.error(`Limit is ${max} images — please remove some first.`);
      return;
    }
    // Paint blob previews instantly. We store them under a temporary key
    // (`pending:<idx>`) and rewrite to the server URL once the upload
    // resolves so the SortableContext key stays stable.
    const pendingBlobs = files.map((f) => URL.createObjectURL(f));
    const pendingKeys = pendingBlobs.map((_, i) => `pending:${Date.now()}-${i}`);
    setBlobMap((prev) => {
      const next = { ...prev };
      pendingKeys.forEach((k, i) => (next[k] = pendingBlobs[i]));
      return next;
    });
    onChange([...value, ...pendingKeys]);

    setUploading(true);
    try {
      const results = await uploadImages(files, subdir);
      const newUrls = results.map((r) => r.url);
      // Swap the pending keys for real server URLs and re-key the blob map.
      setBlobMap((prev) => {
        const next = { ...prev };
        pendingKeys.forEach((k, i) => {
          if (newUrls[i]) {
            next[newUrls[i]] = next[k];
            delete next[k];
          } else if (next[k]?.startsWith("blob:")) {
            URL.revokeObjectURL(next[k]);
            delete next[k];
          }
        });
        return next;
      });

      // Automatically assign the currently selected upload category to all new images
      const updatedCategories = { ...categories };
      newUrls.forEach((url) => {
        if (url) {
          updatedCategories[url] = uploadCategory;
        }
      });
      onCategoriesChange(updatedCategories);

      // Replace the pending keys we appended above with their final server URLs.
      const merged = [...value, ...pendingKeys];
      const swapped = merged.map((entry) => {
        const idx = pendingKeys.indexOf(entry);
        return idx === -1 ? entry : newUrls[idx] ?? entry;
      });
      onChange(swapped);
      toast.success(`${newUrls.length} image${newUrls.length === 1 ? "" : "s"} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Upload failed");
      setBlobMap((prev) => {
        const next = { ...prev };
        for (const k of pendingKeys) {
          if (next[k]?.startsWith("blob:")) URL.revokeObjectURL(next[k]);
          delete next[k];
        }
        return next;
      });
      onChange(value);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = value.indexOf(active.id);
    const newIndex = value.indexOf(over.id);
    onChange(arrayMove(value, oldIndex, newIndex));
  };

  const removeAt = (idx) => {
    const removed = value[idx];
    if (blobMap[removed]?.startsWith("blob:")) {
      URL.revokeObjectURL(blobMap[removed]);
      setBlobMap((prev) => {
        const next = { ...prev };
        delete next[removed];
        return next;
      });
    }
    onChange(value.filter((_, i) => i !== idx));

    // Cleanup the categories object when an image is deleted
    if (categories && removed in categories) {
      const nextCats = { ...categories };
      delete nextCats[removed];
      onCategoriesChange(nextCats);
    }
  };

  const handleCategoryChange = (url, newCat) => {
    onCategoriesChange({
      ...categories,
      [url]: newCat,
    });
  };

  const resolveSrc = (url) => {
    const blob = blobMap[url];
    if (blob) return blob;
    if (url?.startsWith("pending:")) return ""; // shouldn't happen — blobMap covers it
    return assetUrl(url);
  };

  return (
    <div className="space-y-1.5">
      {label && (
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <label className="block text-xs font-medium uppercase tracking-wider text-text-secondary">
            {label}
            <span className="ml-2 normal-case tracking-normal text-text-muted">
              ({value.length}/{max})
            </span>
          </label>

          {/* Batch category selector */}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-medium text-text-muted">Upload Category:</span>
            <select
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
              className="rounded border border-border-subtle bg-bg-elevated px-2 py-0.5 text-[11px] font-medium text-text-primary outline-none focus:ring-1 focus:ring-accent cursor-pointer"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value} className="bg-bg-elevated text-text-primary">
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/avif"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={value} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {value.map((url, idx) => (
              <SortableTile
                key={url}
                id={url}
                src={resolveSrc(url)}
                pending={url?.startsWith("pending:")}
                index={idx}
                category={categories?.[url] ?? "general"}
                onCategoryChange={(newCat) => handleCategoryChange(url, newCat)}
                onRemove={() => removeAt(idx)}
              />
            ))}
            {value.length < max && (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border-subtle bg-bg-elevated-2 text-text-muted hover:border-accent hover:text-accent disabled:opacity-50"
              >
                {uploading ? (
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <Upload className="h-5 w-5" />
                )}
                <span className="text-xs">{uploading ? "Uploading…" : "Add"}</span>
              </button>
            )}
          </div>
        </SortableContext>
      </DndContext>

      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

function SortableTile({ id, src, pending, index, category, onCategoryChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative aspect-square overflow-hidden rounded-lg border border-border-subtle bg-bg-elevated-2",
        isDragging && "opacity-50 ring-2 ring-accent"
      )}
    >
      <img src={src} alt="" className="h-full w-full object-cover" />
      <span className="absolute left-2 top-2 rounded bg-bg-base/80 px-1.5 py-0.5 text-[10px] font-medium text-text-primary backdrop-blur">
        #{index + 1}
      </span>
      {pending && (
        <div className="absolute inset-0 flex items-center justify-center bg-bg-base/40 backdrop-blur-sm">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent text-text-primary" />
        </div>
      )}

      {/* Sleek inline category selector */}
      {!pending && (
        <div className="absolute bottom-1.5 left-1.5 right-1.5 z-10 transition-opacity duration-300 opacity-90 group-hover:opacity-100">
          <select
            value={category}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="w-full rounded border border-border-subtle bg-bg-base/85 px-1.5 py-0.5 text-[10px] font-semibold text-text-primary outline-none focus:ring-1 focus:ring-accent backdrop-blur cursor-pointer hover:bg-bg-base transition-colors"
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value} className="bg-bg-elevated text-text-primary">
                {c.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="absolute inset-0 flex items-start justify-end gap-1 bg-gradient-to-b from-black/60 via-transparent to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100 pb-12">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="cursor-grab rounded bg-bg-base/80 p-1 text-text-secondary backdrop-blur hover:text-text-primary active:cursor-grabbing"
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="rounded bg-danger/80 p-1 text-white backdrop-blur hover:bg-danger"
          aria-label="Remove image"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
